/* eslint-disable prettier/prettier */
import React, { memo, useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Table, Tooltip, ConfigProvider } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import FilterDropdown from 'common/components/filter';
import generatePath from '~settings/routing';
import SvgFormat from 'common/helpers/svgFormat';
import { dayFormatType, formatDay } from '~helpers/day';
import { sortByMapping, sortDirectionMapping } from '~constants/index';
import { registeredTypes, landlordStatus, contractStatus } from '~constants/landlord';
import { QUERY_LANDLORD_NAMES } from '../queries';
import { message } from 'antd';

import BlankPage from '../blank-page';
import {
  FilterIcon,
  TableSearchFilter,
  DropFilterIcon,
} from 'common/components/table-filter-component';
const CHECKED_COLOR = '#38b2a6';
const UNCHECKED_COLOR = '#979797';

const EXPIRED_COLOR = '#63656a';
const ACTIVE_COLOR = '#419061';
const INACTIVE_COLOR = '#f9a600';

const registeredOpts = [
  {
    text: 'Self-Registered',
    value: registeredTypes.SELF_REGISTERED,
  },
  {
    text: 'Staff Created',
    value: registeredTypes.STAFF_CREATED,
  },
];
const verificationStatusOpts = [
  {
    text: 'Onboarding',
    value: landlordStatus.ONBOARDING,
  },
  {
    text: 'Pending',
    value: landlordStatus.PENDING,
  },
  {
    text: 'Verified',
    value: landlordStatus.VERIFIED,
  },
  {
    text: 'In consultation',
    value: landlordStatus.IN_CONSULTATION,
  },
  {
    text: 'Lost',
    value: landlordStatus.LOST,
  },
  {
    text: 'Rejected',
    value: landlordStatus.REJECTED,
  },
  {
    text: 'Unpublished',
    value: landlordStatus.UNPUBLISHED,
  },
];

const LandlordTable = memo(
  ({
    type,
    loading,
    filters,
    total,
    setFilters,
    landlordNameList,
    dataSource,
    setCurrentLandlord,
  }) => {
    const { t } = useTranslation();
    const history = useHistory();

    const landlordNameRef = useRef(null);

    const [landlordNameVisible, setLandlordNameVisible] = useState(false);
    const [registerVisible, setRegisterVisible] = useState(false);
    const [verifyVisible, setVerifyVisible] = useState(false);
    const [landlordNamespage, setLandlordNamesPage] = useState(1);
    const [landlordNames, setLandlordNames] = useState([]);
    const [query, setQuery] = useState('');
    const landlordNamePageSize = 20;

    const hasMore = useRef(true);

    const { loading: isLandlordNamesloading, data: landlordNamesData } = useQuery(QUERY_LANDLORD_NAMES, {
      variables: {
        pageNumber: landlordNamespage,
        pageSize: landlordNamePageSize,
        query,
      },
      fetchPolicy: 'network-only',
        
      onError: e => {
        message.error(e?.toString());
      },
    });

    const handleTableChange = useCallback(
      ({ current, pageSize }, tempFilters, sorter) => {
        const params = {};

        if (tempFilters?.status) {
          params.statuses = tempFilters?.status;
        }

        if (sorter.columnKey) {
          params.sortBy = sortByMapping[sorter.columnKey];
          params.sortDirection = sortDirectionMapping[sorter.order];
        } else {
          params.sortBy = undefined;
          params.sortDirection = undefined;
        }

        if (current !== filters.pageNumber) {
          params.pageNumber = current;
          params.pageSize = pageSize;
        }

        if (pageSize !== filters.pageSize) {
          params.pageSize = pageSize;
          params.pageNumber = 1;
        }

        setFilters(params);
      },
      [filters, setFilters],
    );

    const handleLandlordSearch = (value) => {
      setLandlordNamesPage(1);
      setLandlordNames([]);
      setQuery(value);
      hasMore.current = true;
    };

    const handleScrollEnd = () => {
      if (!hasMore.current) return;
      setLandlordNamesPage(prevPage => prevPage + 1);
    };

    const handleRowClick = useCallback(
      currRecord => {
        if (currRecord.status !== landlordStatus.ONBOARDING) {
          history.push(generatePath('landlord', { landlordSlug: currRecord.slug }));
          setCurrentLandlord(currRecord);
        }
      },
      [history, setCurrentLandlord],
    );

    useEffect(() => {
      if (!isLandlordNamesloading) {
        const result = landlordNamesData?.search?.edges?.map(item => item.node) || [];
        if (result.length < landlordNamePageSize) {
          hasMore.current = false;
        } else {
          hasMore.current = true;
        }
        if (result.length) {
          setLandlordNames(prev => [...prev, ...result]);
        }
      }
    }, [!!landlordNamesData]);

    const generateColumns = useMemo(() => {
      const findNameByVal = (val, source) =>
        (source?.find(item => item.value === val) ?? {})['text'];

      const allColumns = [
        {
          title: t('cms.landlord.landlord_name.table'),
          dataIndex: 'name',
          key: 'name',
          width: '20%',
          render: (_, record) => {
            const name =
              record.businessType === 'INDIVIDUAL'
                ? `${record?.firstName} ${record?.lastName}`
                : record.name;

            return (
              <Tooltip placement="topLeft" title={name}>
                {name}
              </Tooltip>
            );
          },
          filterDropdown: (
            <div>
              <label ref={landlordNameRef} />
              <TableSearchFilter
                active={landlordNameVisible}
                targetInput={landlordNameRef.current}
                optionValueProp="slug"
                isFetching={isLandlordNamesloading}
                options={landlordNames}
                selectedValue={filters.slugs}
                onChange={slugs =>
                  setFilters({
                    slugs,
                    pageNumber: 1,
                  })
                }
                onSearch={handleLandlordSearch}
                onScrollEnd={handleScrollEnd}
                onClose={() => setLandlordNameVisible(false)}
              />
            </div>
          ),
          filterIcon: (
            <span className="ant-table-filter-icon">
              <FilterIcon color={filters.slugs?.length ? CHECKED_COLOR : UNCHECKED_COLOR} />
            </span>
          ),
          filterDropdownVisible: landlordNameVisible,
          onFilterDropdownVisibleChange: visible => {
            setLandlordNameVisible(visible);
          },
        },
        {
          title: t('cms.landlord.registered.table'),
          dataIndex: 'registerType',
          key: 'registerType',
          width: '18%',
          render: text => findNameByVal(text, registeredOpts),
          filterDropdown: (
            <FilterDropdown
              filterDropdownVisible={verifyVisible}
              options={registeredOpts.map(item =>
                Object.assign({ value: item.value }, { label: item.text }),
              )}
              closeBox={() => {
                setVerifyVisible(false);
              }}
              submitHandle={registerType => {
                setFilters({
                  registerTypes: registerType,
                  pageNumber: 1,
                });
              }}
            />
          ),
          filterIcon: (
            <span className="ant-table-filter-icon">
              <DropFilterIcon
                color={filters.registerTypes?.length > 0 ? CHECKED_COLOR : UNCHECKED_COLOR}
              />
            </span>
          ),
          filterDropdownVisible: verifyVisible,
          onFilterDropdownVisibleChange: visible => {
            setVerifyVisible(visible);
          },
        },
        {
          title: t('cms.landlord.verification_status.table'),
          dataIndex: 'status',
          key: 'status',
          width: '17%',
          filterIcon: (
            <span className="ant-table-filter-icon">
              <DropFilterIcon
                color={filters.statuses?.length > 0 ? CHECKED_COLOR : UNCHECKED_COLOR}
              />
            </span>
          ),
          render: text => findNameByVal(text, verificationStatusOpts),
          filterDropdown: (
            <FilterDropdown
              filterDropdownVisible={registerVisible}
              options={verificationStatusOpts.map(item =>
                Object.assign({ value: item.value }, { label: item.text }),
              )}
              closeBox={() => {
                setRegisterVisible(false);
              }}
              submitHandle={registerType => {
                setFilters({
                  statuses: registerType,
                  pageNumber: 1,
                });
              }}
            />
          ),

          filterDropdownVisible: registerVisible,
          onFilterDropdownVisibleChange: visible => {
            setRegisterVisible(visible);
          },
        },
        {
          title: t('cms.landlord.property_count.table'),
          dataIndex: 'propertiesCount',
          width: '15%',
          key: 'propertiesCount',
          render: text => text || 0,
        },
        {
          title: t('cms.landlord.business_opportunity.table'),
          dataIndex: 'businessScale',
          width: '15%',
          key: 'businessScale',
          render: text => text || 0,
        },
        {
          title: t('cms.landlord.contract_attached.table'),
          dataIndex: 'contractAttachedStatus',
          width: '15%',
          align: 'center',
          key: 'contractAttachedStatus',
          render: text => {
            switch (text) {
              case contractStatus.EXPIRED:
                return <CloseOutlined style={{ color: EXPIRED_COLOR }} />;
              case contractStatus.ACTIVE:
                return <CheckOutlined style={{ color: ACTIVE_COLOR }} />;
              case contractStatus.INACTIVE:
                return <CheckOutlined style={{ color: INACTIVE_COLOR }} />;
              default:
                return null;
            }
          },
        },
      ];

      const onboardingColumns = [
        {
          title: t('cms.landlord.landlord_name.table'),
          dataIndex: 'name',
          key: 'name',
          render: name => (
            <Tooltip placement="topLeft" title={name}>
              {name}
            </Tooltip>
          ),
          width: '40%',
        },
        {
          title: t('cms.landlord.verification_status.table'),
          dataIndex: 'status',
          key: 'status',
          width: '38%',
          render: text => findNameByVal(text, verificationStatusOpts),
        },
        {
          title: t('cms.landlord.created_at.table'),
          dataIndex: 'createdAt',
          width: '22%',
          sorter: true,
          sortOrder: sortDirectionMapping[filters?.sortDirection],
          render: text => (text ? formatDay(dayFormatType.UKFORMAT, text) : ''),
        },
      ];

      if (type === 'all') {
        return allColumns;
      } else if (type === 'onboarding') {
        return onboardingColumns;
      } else {
        return [];
      }
    }, [
      t,
      landlordNameList,
      filters.landlordSlugs,
      filters.registerTypes,
      filters.statuses,
      filters?.sortDirection,
      filters.slugs,
      landlordNameVisible,
      registerVisible,
      verifyVisible,
      type,
      setFilters,
      landlordNames,
      isLandlordNamesloading,
    ]);

    return (
      <ConfigProvider
        renderEmpty={() => (
          <BlankPage
            svg={
              <If condition={!loading}>
                <SvgFormat type="no-pending" className={'landlord-management__noPending'} />
              </If>
            }
            descKey={!loading ? 'cms.landlords.blank_page.no_filters' : null}
          />
        )}
      >
        <div className="landlord-table">
          <Table
            rowKey="id"
            className="table-list"
            loading={loading}
            columns={generateColumns}
            dataSource={dataSource}
            pagination={{
              current: filters.pageNumber,
              pageSize: filters.pageSize,
              showSizeChanger: true,
              total,
            }}
            onChange={handleTableChange}
            onRow={record => ({
              onClick: () => handleRowClick(record), // click row
            })}
          />
        </div>
      </ConfigProvider>
    );
  },
);

LandlordTable.propTypes = {
  type: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  filters: PropTypes.object.isRequired,
  total: PropTypes.number.isRequired,
  dataSource: PropTypes.array.isRequired,
  setFilters: PropTypes.func.isRequired,
  landlordNameList: PropTypes.array.isRequired,
  setCurrentLandlord: PropTypes.func.isRequired,
};

LandlordTable.defaultProps = {
  type: 'all',
  loading: false,
  filters: {},
  total: 0,
  dataSource: [],
  setFilters: () => {},
  landlordNameList: [],
  setCurrentLandlord: () => {},
};

LandlordTable.displayName = 'LandlordTable';

export default LandlordTable;
